"use client";

import { FC, PropsWithChildren, useEffect } from "react";
import Intercom from "@intercom/messenger-js-sdk";

import { useAuth } from "@auth/client-sdk-react";
import { useAPI } from "apps/website/hooks/useAPI";

const INTERCOM_APP_ID = "tis5m73e";
const INTERCOM_REGION = "eu";

const displayLoggedInChat = async (
  issueChatToken: (phoneNumber: string, name: string) => Promise<string>,
  id: string,
  phoneNumber: string,
  name: string,
) => {
  try {
    /**
  * This creates a token for the Intercom chatbot.
  * Intercom drops a cookie (not containing the token) on the browser, which signifies
  * to Intercom that the user is logged in. This lasts for 7 days.
  * After the 7 days, the cookie will be deleted, and the user will see an anonymous chatbot,
  * if they've left the window open (Intercom handles all this).
  * If they have an activity in the chat window after the 7 days,
  * they'll see the authenticated chatbot for 1 hour afterwards,
  * but will not be allocated a fresh session cookie, (all handled by Intercom).
  * A fresh cookie will automatically be issued when we instantiate the Intercom object - as seen below -
  * and they'll get a new cookie then (Intercom handles this).
  * As we instantiate the Intercom object in this component, and this component is rendered on every page,
  * this means that the cookie will be refreshed automatically whenever the user refreshes the page.
  *  The majority of users will likely close the window after chat -
  * and will therefore be issued a new cookie and see the logged in version of the chatbot each time.
   */
    const chatToken = await issueChatToken(phoneNumber, name);

    Intercom({
      app_id: INTERCOM_APP_ID,
      region: INTERCOM_REGION,
      user_id: id,
      intercom_user_jwt: chatToken,
    });
  } catch (error) {
    console.error("Failed to issue chat token, falling back to anonymous chat:", error);
    Intercom({
      app_id: INTERCOM_APP_ID,
      region: INTERCOM_REGION,
    });
  }
};

const displayAnonymousChat = () => {
  Intercom({
    app_id: INTERCOM_APP_ID,
    region: INTERCOM_REGION,
  });

};

export const ChatBot: FC<PropsWithChildren> = () => {
  const { loggedIn, id, issueChatToken, accessToken, iold } = useAuth();
  const api = useAPI();

  useEffect(() => {
    const initializeIntercom = async () => {
      if (iold) {
        return;
      }

      if (!loggedIn || !id || !accessToken) {
        displayAnonymousChat();
        return;
      }

      try {
        const user = await api.CustomerManagement.getSimpleUser();

        const { firstName, lastName, phoneNumber } = user;

        if (!firstName || !lastName || !phoneNumber) {
          /**
       * The non-anonymous version of the chat creates a user in intercom the first time it boots up.
       * We need them to have complete data, so if any of these fields are missing, best to
       * fall back to the anonymous version.
       */
          displayAnonymousChat();
          return;
        }

        const fullName = `${user.firstName} ${user.lastName}`;

        await displayLoggedInChat(
          issueChatToken,
          id,
          phoneNumber,
          fullName,
        );
      } catch (error) {
        console.error("Failed to fetch user data for Intercom, falling back to anonymous chat:", error);
        displayAnonymousChat();
      }
    };

    void initializeIntercom();
  }, [ loggedIn, id, accessToken, api, issueChatToken, iold ]);

  return null;
};
